import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { FixedSizeGrid as Grid } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { debounce } from "lodash";
import {
  FaSearch,
  FaTimes,
  FaUpload,
  FaTrash,
  FaFolder,
  FaList,
  FaTh,
  FaFile,
  FaFileImage,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileCode,
  FaFileAudio,
  FaFileVideo,
  FaFileArchive,
  FaSpinner,
  FaCloudUploadAlt,
} from "react-icons/fa";
import fileManagerService from "../../services/fileManagerService";

const GRID_ITEM_SIZE = 200;
const LIST_ITEM_HEIGHT = 80;

// Inner component that contains all the hooks
const FileManagerContent = ({
  onClose = () => {},
  onSelect = () => {},
  accept = [],
  showPreview = true,
  title = "File Manager",
}) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedFileIndex, setSelectedFileIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");

  // Constants for grid layout
  const GRID_COLUMNS = 4; // Number of columns in grid view
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce((term) => {
        setCurrentPage(1);
        loadFiles(1, term);
      }, 300),
    [loadFiles],
  );

  // Handle search input change
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchQuery(value);
      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        await handleFileUpload(acceptedFiles);
      }
    },
    [handleFileUpload],
  );

  // Use destructuring to ignore isDragActive since we use our own isDragging state
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: true,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
    onDropAccepted: () => setIsDragging(false),
    onDropRejected: () => setIsDragging(false),
    accept:
      accept.length > 0
        ? accept.reduce((acc, type) => {
            acc[`image/${type.toLowerCase()}`] = [];
            acc[`application/${type.toLowerCase()}`] = [];
            return acc;
          }, {})
        : undefined,
  });

  const loadFiles = useCallback(
    async (page = currentPage, search = searchQuery) => {
      try {
        setLoading(true);
        setError("");

        const response = await fileManagerService.getFiles({
          page,
          limit: 50,
          search,
          type: activeCategory !== "all" ? activeCategory : "",
          sort: "createdAt:desc",
        });

        console.log("Files response:", response);
        console.log("Files data structure:", response.data);

        if (response.data && response.data.length > 0) {
          console.log("Sample file object:", response.data[0]);
          console.log("File properties:", Object.keys(response.data[0]));
        } else {
          console.log("No files returned from API");
        }

        // Normalize file objects to ensure they have all required properties
        const normalizedFiles = (response.data || []).map((file) => {
          // Extract name as a string first to avoid type issues
          const fileName =
            typeof file.name === "string"
              ? file.name
              : typeof file.filename === "string"
                ? file.filename
                : typeof file.title === "string"
                  ? file.title
                  : "Unnamed file";

          // Get file type info using the validated fileName
          const typeInfo = fileManagerService.getFileTypeInfo(fileName);

          // Ensure each file has the required properties with fallbacks
          return {
            id:
              file.id ||
              file._id ||
              `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: fileName,
            url: file.url || file.path || "",
            size: typeof file.size === "number" ? file.size : 0,
            type:
              file.type ||
              file.fileType ||
              file.mimeType ||
              typeInfo.type ||
              "unknown",
            category: file.category || typeInfo.category || "UNKNOWN",
            // Keep all original properties
            ...file,
          };
        });

        console.log("Normalized files:", normalizedFiles);

        setFiles(normalizedFiles);
        setTotalRecords(response.total || 0);
        setCurrentPage(page);
      } catch (error) {
        console.error("Error loading files:", error);
        setError("Failed to load files. Please try again.");
        setFiles([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, searchQuery, activeCategory],
  );

  const handleFileUpload = useCallback(
    async (filesToUpload) => {
      if (!filesToUpload || filesToUpload.length === 0) return;

      try {
        setUploading(true);
        setError("");

        const uploadPromises = filesToUpload.map(async (file) => {
          if (
            accept.length > 0 &&
            !fileManagerService.isValidFileType(file, accept)
          ) {
            throw new Error(
              `File type ${fileManagerService.getFileExtension(file.name)} is not allowed`,
            );
          }

          const formData = new FormData();
          formData.append("fmFile", file);

          const fileId = `${file.name}-${Date.now()}`;
          setUploadProgress((prev) => ({ ...prev, [fileId]: 0 }));

          try {
            const response = await fileManagerService.uploadFile(formData, {
              type: fileManagerService.getFileTypeInfo(file.name).category,
              name: file.name,
              size: file.size,
              onUploadProgress: (progress) => {
                setUploadProgress((prev) => ({ ...prev, [fileId]: progress }));
              },
            });

            console.log("Upload successful for file:", file.name, response);

            setUploadProgress((prev) => {
              const newProgress = { ...prev };
              delete newProgress[fileId];
              return newProgress;
            });

            return response;
          } catch (error) {
            console.error("Error in individual file upload:", error);
            setUploadProgress((prev) => {
              const newProgress = { ...prev };
              delete newProgress[fileId];
              return newProgress;
            });
            throw error;
          }
        });

        try {
          const results = await Promise.all(uploadPromises);
          console.log("All uploads completed successfully:", results);

          setError("");

          await loadFiles();
        } catch (error) {
          console.error("Error in Promise.all:", error);
          throw error;
        }
      } catch (error) {
        console.error("Error uploading files (outer catch):", error);
        setError(error.message || "Failed to upload files. Please try again.");
      } finally {
        setUploading(false);
      }
    },
    [loadFiles],
  );

  const handleFileSelect = (fileIndex) => {
    setSelectedFileIndex(fileIndex === selectedFileIndex ? null : fileIndex);
  };

  const handleUseSelectedFile = useCallback(() => {
    if (selectedFileIndex !== null && files[selectedFileIndex]) {
      const selectedFile = files[selectedFileIndex];

      onSelect({
        file: selectedFile,
        fmFile: selectedFile,
        url: selectedFile.url,
        name: selectedFile.name || selectedFile.title,
        type: selectedFile.type,
        size: selectedFile.size,
      });

      onClose();
    }
  }, [selectedFileIndex, files, onSelect, onClose]);

  const getFileIcon = (file) => {
    const typeInfo = fileManagerService.getFileTypeInfo(file.name || file.type);

    switch (typeInfo.category) {
      case "IMAGE":
        return FaFileImage;
      case "DOCUMENT":
        return typeInfo.type === "PDF" ? FaFilePdf : FaFileWord;
      case "SPREADSHEET":
        return FaFileExcel;
      case "CODE":
        return FaFileCode;
      case "AUDIO":
        return FaFileAudio;
      case "VIDEO":
        return FaFileVideo;
      case "ARCHIVE":
        return FaFileArchive;
      default:
        return FaFile;
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      if (selectedFileIndex !== null) {
        handleUseSelectedFile();
      }
    } else if (event.key === "Escape") {
      onClose();
    }
  };

  const GridItem = ({ columnIndex, rowIndex, style, data }) => {
    const { files, selectedFileIndex } = data;
    const index = rowIndex * GRID_COLUMNS + columnIndex;
    const file = files[index];

    if (!file) return <div style={style} />;

    const isSelected = selectedFileIndex === index;
    const Icon = getFileIcon(file);
    const typeInfo = fileManagerService.getFileTypeInfo(file.name || file.type);

    return (
      <div style={style} className="p-2">
        <div
          className={`relative group border-2 rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
            isSelected
              ? "border-blue-500 bg-blue-50"
              : "border-gray-200 hover:border-gray-300"
          }`}
          onClick={() => handleFileSelect(index)}
        >
          <div className="flex flex-col items-center space-y-2">
            {/* File preview or icon */}
            {showPreview && typeInfo.category === "IMAGE" && file.url ? (
              <div className="w-16 h-16 rounded overflow-hidden">
                <img
                  src={fileManagerService.getThumbnailUrl(file, {
                    width: 64,
                    height: 64,
                  })}
                  alt={file.title || file.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "block";
                  }}
                />
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{ display: "none" }}
                >
                  <Icon size={32} style={{ color: typeInfo.color }} />
                </div>
              </div>
            ) : (
              <Icon size={32} style={{ color: typeInfo.color }} />
            )}

            {/* File name */}
            <div
              className="text-xs text-center text-gray-700 truncate w-full"
              title={file.title || file.name}
            >
              {file.title || file.name}
            </div>

            {/* File size */}
            <div className="text-xs text-gray-500">
              {fileManagerService.formatFileSize(file.size)}
            </div>
          </div>

          {/* Selection checkbox - part of the clickable area */}
          <div className="absolute top-2 left-2 z-20">
            <input
              type="checkbox"
              checked={isSelected}
              readOnly
              name="file-selection"
              className="bg-white/70 rounded p-0.5 focus:outline-none pointer-events-none"
            />
          </div>
        </div>
      </div>
    );
  };

  // List item renderer for virtualized list view
  const ListItem = ({ rowIndex, style, data }) => {
    const { files, selectedFileIndex } = data;
    const file = files[rowIndex];
    const index = rowIndex;
    if (!file) return null;

    const isSelected = selectedFileIndex === index;
    const Icon = getFileIcon(file);
    const typeInfo = fileManagerService.getFileTypeInfo(file.name);

    return (
      <div style={style} className="px-4">
        <div
          className={`flex items-center space-x-4 p-3 border rounded-lg cursor-pointer transition-all hover:shadow-sm ${
            isSelected
              ? "border-blue-500 bg-blue-50"
              : "border-gray-200 hover:border-gray-300"
          }`}
          onClick={() => handleFileSelect(index)}
        >
          <div className="flex-shrink-0">
            <input
              type="checkbox"
              checked={isSelected}
              readOnly
              name="file-selection"
              className="pointer-events-none"
            />
          </div>

          {/* File icon/preview */}
          <div className="flex-shrink-0">
            {showPreview && typeInfo.category === "IMAGE" && file.url ? (
              <div className="w-12 h-12 rounded overflow-hidden">
                <img
                  src={fileManagerService.getThumbnailUrl(file, {
                    width: 48,
                    height: 48,
                  })}
                  alt={file.title || file.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{ display: "none" }}
                >
                  <Icon size={24} style={{ color: typeInfo.color }} />
                </div>
              </div>
            ) : (
              <Icon size={24} style={{ color: typeInfo.color }} />
            )}
          </div>

          {/* File info */}
          <div className="flex-1 min-w-0">
            <div
              className="text-sm font-medium text-gray-900 truncate"
              title={file.title || file.name}
            >
              {file.title || file.name}
            </div>
            <div className="text-sm text-gray-500">
              {typeInfo.type} • {fileManagerService.formatFileSize(file.size)}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                // handleDeleteFile(file);
              }}
              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
              title="Delete"
            >
              <FaTrash size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    loadFiles();
  }, [activeCategory, loadFiles]);

  useEffect(() => {
    debouncedSearch(searchQuery);
    return () => debouncedSearch.cancel();
  }, [searchQuery, debouncedSearch]);

  const categories = [
    { key: "all", label: "All Files", icon: FaFolder },
    { key: "IMAGE", label: "Images", icon: FaFileImage },
    { key: "DOCUMENT", label: "Documents", icon: FaFileWord },
    { key: "SPREADSHEET", label: "Spreadsheets", icon: FaFileExcel },
    { key: "CODE", label: "Code", icon: FaFileCode },
    { key: "AUDIO", label: "Audio", icon: FaFileAudio },
    { key: "VIDEO", label: "Video", icon: FaFileVideo },
    { key: "ARCHIVE", label: "Archives", icon: FaFileArchive },
  ];

  const gridColumnCount = Math.max(1, Math.floor(800 / GRID_ITEM_SIZE));
  const gridRowCount = Math.max(1, Math.ceil(files.length / gridColumnCount));

  // Log file data for debugging
  console.log("Files data:", {
    filesLength: files.length,
    firstFile: files[0],
    gridColumnCount,
    gridRowCount,
  });

  // We don't need to filter files client-side since the API handles search
  // This was previously unused, so removing the filteredFiles variable

  // Define accept types based on props
  const allowedTypes = accept || [];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div
        {...getRootProps()}
        className={`bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col relative ${
          isDragging ? "ring-2 ring-blue-500" : ""
        }`}
      >
        <input {...getInputProps()} />

        {/* Drag overlay */}
        {isDragging && (
          <div className="absolute inset-0 bg-blue-50 bg-opacity-90 flex items-center justify-center z-10 rounded-lg">
            <div className="text-center">
              <FaCloudUploadAlt
                size={48}
                className="text-blue-500 mx-auto mb-4"
              />
              <p className="text-lg font-medium text-blue-700">
                Drop files here to upload
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <FaSearch
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={14}
              />
              <input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Upload button */}
            <label className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer transition-colors">
              <FaUpload size={14} />
              <span>Upload</span>
              <input
                type="file"
                multiple
                onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                className="hidden"
                accept={
                  allowedTypes.length > 0
                    ? allowedTypes.map((type) => `.${type}`).join(",")
                    : undefined
                }
              />
            </label>
          </div>

          <div className="flex items-center space-x-4">
            {/* View mode toggle */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-md ${viewMode === "grid" ? "bg-blue-100 text-blue-700" : "text-gray-600 hover:bg-gray-100"}`}
                title="Grid view"
              >
                <FaTh size={14} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-md ${viewMode === "list" ? "bg-blue-100 text-blue-700" : "text-gray-600 hover:bg-gray-100"}`}
                title="List view"
              >
                <FaList size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="flex items-center space-x-2 p-4 border-b overflow-x-auto">
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.key}
                onClick={() => setActiveCategory(category.key)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  activeCategory === category.key
                    ? "bg-blue-100 text-blue-700"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <Icon size={14} />
                <span>{category.label}</span>
              </button>
            );
          })}
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Upload progress */}
        {Object.keys(uploadProgress).length > 0 && (
          <div className="mx-4 mt-4 space-y-2">
            {Object.entries(uploadProgress).map(([fileId, progress]) => (
              <div key={fileId} className="bg-gray-50 rounded-md p-3">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                  <span>Uploading {fileId.split("-")[0]}...</span>
                  <span>{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Content */}
        <div
          className="flex-1 overflow-hidden"
          style={{
            minHeight: "400px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <FaSpinner className="animate-spin text-gray-400" size={24} />
              <span className="ml-2 text-gray-600">Loading files...</span>
            </div>
          ) : files.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <FaFolder size={48} className="mb-4" />
              <p className="text-lg font-medium">No files found</p>
              <p className="text-sm">Upload some files to get started</p>
            </div>
          ) : (
            <div style={{ flex: 1, position: "relative", minHeight: "300px" }}>
              <div
                style={{
                  position: "absolute",
                  top: "0px",
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
              >
                <AutoSizer>
                  {({ height, width }) => {
                    console.log("AutoSizer dimensions:", { height, width });
                    // Recalculate grid dimensions based on actual width
                    const actualColumnCount = Math.max(
                      1,
                      Math.floor(width / GRID_ITEM_SIZE),
                    );
                    const actualRowCount = Math.max(
                      1,
                      Math.ceil(files.length / actualColumnCount),
                    );
                    console.log("Actual grid dimensions:", {
                      actualColumnCount,
                      actualRowCount,
                    });

                    return viewMode === "grid" ? (
                      <div style={{ height: "100%", width: "100%" }}>
                        <Grid
                          columnCount={actualColumnCount}
                          columnWidth={GRID_ITEM_SIZE}
                          height={height}
                          rowCount={actualRowCount}
                          rowHeight={GRID_ITEM_SIZE}
                          width={width}
                          itemData={{
                            files,
                            selectedFileIndex,
                          }}
                        >
                          {GridItem}
                        </Grid>
                      </div>
                    ) : (
                      <Grid
                        columnCount={1}
                        columnWidth={width}
                        height={height}
                        rowCount={files.length}
                        rowHeight={LIST_ITEM_HEIGHT}
                        width={width}
                        itemData={{
                          files,
                          selectedFileIndex,
                        }}
                      >
                        {ListItem}
                      </Grid>
                    );
                  }}
                </AutoSizer>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            {selectedFileIndex !== null ? (
              <span>1 file selected</span>
            ) : (
              <span>Total: {totalRecords} files</span>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>

            <button
              onClick={handleUseSelectedFile}
              disabled={selectedFileIndex === null}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Use Selected File
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper component that handles conditional rendering
const FileManager = ({
  isOpen = false,
  onClose = () => {},
  onSelect = () => {},
  accept = [],
  showPreview = true,
  title = "File Manager",
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <FileManagerContent
      onClose={onClose}
      onSelect={onSelect}
      accept={Array.isArray(accept) ? accept : []}
      showPreview={showPreview}
      title={title}
    />
  );
};

export default FileManager;
